import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Configuration
 * 
 * This configures the default behavior for all queries and mutations
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 30 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      
      // Data is considered fresh for 5 minutes
      staleTime: 1000 * 60 * 5, // 5 minutes
      
      // Retry failed requests 2 times
      retry: 2,
      
      // Retry delay increases exponentially
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus
      refetchOnWindowFocus: false,
      
      // Refetch on mount if data is stale
      refetchOnMount: true,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Keep unused data in cache
      refetchInterval: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Query Keys Factory
 * Provides consistent query keys across the application
 */
export const queryKeys = {
  movies: {
    all: ['movies'] as const,
    list: (filters?: Record<string, any>) => ['movies', 'list', filters] as const,
    detail: (id: string | number) => ['movies', 'detail', id] as const,
    banner: (language: string) => ['movies', 'banner', language] as const,
    search: (query: string, language: string) => ['movies', 'search', query, language] as const,
    popular: (language: string, page: number) => ['movies', 'popular', language, page] as const,
    exclusive: (language: string, page: number) => ['movies', 'exclusive', language, page] as const,
  },
  watchlist: {
    all: ['watchlist'] as const,
    list: () => ['watchlist', 'list'] as const,
  },
  user: {
    all: ['user'] as const,
    profile: () => ['user', 'profile'] as const,
  },
} as const;
